/**
 * LA PLANTILLA DE MADERINCO EN EL ARMADOR (MADERINCO-PANTALLA-ITEMS-T1, T129). Sin DOM.
 *
 * Contrato: GET /api/v1/presupuestos/plantilla (nexo-api 551, MADERINCO-SECCIONES-API-T1) -> {catalogoSha, totalItems,
 * secciones[{id, clave, nombre, orden, nombresOrigen, items[{id, fuenteId, libro, hoja, celda, tipo, descripcion, unidad,
 * cantidadRef, precioRef, subtotalRef, origen}]}]}. Solo el negocio demo (D-126); fuera, PUERTA_CERRADA.
 *
 * Que hace: ordena las secciones (Cubierta primero lo pone el servidor con "orden"), elige los items sugeridos de cada
 * una, convierte un item de la plantilla en un item del armador (unidad de la lista cerrada del guardado por pasos) y
 * valida el formulario con un mensaje por campo.
 *
 * @module application/presupuestos/plantilla
 */

import { UNIDADES } from './porPaso.js';

const de = (o, ...k) => { for (const c of k) if (o && o[c] !== undefined && o[c] !== null) return o[c]; return null; };
const lista = (v) => (Array.isArray(v) ? v : []);
const numOrNull = (v) => (v === null || v === undefined || v === '' || Number.isNaN(Number(v)) ? null : Number(v));

/** El producto casado de un item: undefined si el campo no viene, null si viene vacio, el id si viene. */
function productoDelItem(it) {
  if (!it || (!('producto' in it) && !('Producto' in it))) return undefined;
  const p = it.producto ?? it.Producto;
  if (!p) return null;
  const id = p.id ?? p.Id;
  return id ? String(id) : null;
}

/** La respuesta del GET lista para pintar: secciones por orden, items en el orden del catalogo. */
export function normalizarPlantilla(data) {
  const d = data || {};
  const secciones = lista(de(d, 'secciones', 'Secciones')).map((s) => ({
    id: String(de(s, 'id', 'Id') ?? ''),
    clave: String(de(s, 'clave', 'Clave') ?? ''),
    nombre: String(de(s, 'nombre', 'Nombre') ?? ''),
    orden: Number(de(s, 'orden', 'Orden')) || 0,
    // REPLICAR-PRESUPUESTO-T1: los nombres con que la seccion aparece en los libros, para casar un presupuesto anterior.
    nombresOrigen: lista(de(s, 'nombresOrigen', 'NombresOrigen')).map(String),
    items: lista(de(s, 'items', 'Items')).map((it) => ({
      id: String(de(it, 'id', 'Id') ?? ''),
      // MADERINCO-SELECCION-POR-TIPO-T1: el id del catalogo aprobado; el arbol de Cubierta se mapea por aqui.
      fuenteId: String(de(it, 'fuenteId', 'FuenteId') ?? ''),
      descripcion: String(de(it, 'descripcion', 'Descripcion') ?? '').trim(),
      unidad: de(it, 'unidad', 'Unidad'),
      cantidadRef: numOrNull(de(it, 'cantidadRef', 'CantidadRef')),
      precioRef: numOrNull(de(it, 'precioRef', 'PrecioRef')),
      tipo: String(de(it, 'tipo', 'Tipo') ?? ''),
      // MADERINCO-PLANTILLAS-UX-T1: libro y hoja sueltos, para saber que secciones aparecen juntas (guiaPlantilla.js).
      libro: String(de(it, 'libro', 'Libro') ?? ''),
      hoja: String(de(it, 'hoja', 'Hoja') ?? ''),
      fuente: [de(it, 'libro', 'Libro'), de(it, 'hoja', 'Hoja'), de(it, 'celda', 'Celda')].filter(Boolean).join(' · '),
      // MADERINCO-CATALOGO-UNICO-T1 (nexo-api 577): el producto de Mis items casado con el item. undefined = la API no
      // lo manda (no se sabe); null = la API dice que aun no tiene producto; si no, su id.
      productoId: productoDelItem(it)
    })).filter((it) => it.descripcion)
  })).filter((s) => s.nombre).sort((a, b) => a.orden - b.orden);
  return {
    sha: String(de(d, 'catalogoSha', 'CatalogoSha') ?? ''),
    total: Number(de(d, 'totalItems', 'TotalItems')) || secciones.reduce((n, s) => n + s.items.length, 0),
    secciones
  };
}

/** La unidad del catalogo en la lista cerrada (M2 -> m2, Und -> und, Gnral -> gnral). null si no cabe. */
export function unidadDeLista(u) {
  const x = String(u ?? '').trim().toLowerCase().replace(/[.\s²]/g, (c) => (c === '²' ? '2' : ''));
  const alias = { un: 'und', u: 'und', unid: 'und', unidad: 'und', gl: 'gnral', glb: 'gnral', global: 'gnral', mts: 'm', mt: 'm' };
  const v = alias[x] || x;
  return UNIDADES.includes(v) ? v : null;
}

/** Los sugeridos de una seccion: los primeros n con precio de referencia (el resto aparece con "ver todos"). */
export function sugeridos(seccion, n = 5) {
  return lista(seccion?.items).filter((it) => (it.precioRef || 0) > 0).slice(0, n);
}

/** Un item de la plantilla como item del armador. Sin producto: la unidad tiene que ser de la lista cerrada. */
export function itemDesdePlantilla(it) {
  const unidad = unidadDeLista(it?.unidad) || 'und';
  return {
    titulo: it?.descripcion || '',
    productoId: null,
    unidad,
    cantidad: it?.cantidadRef && it.cantidadRef > 0 ? it.cantidadRef : 1,
    precioUnit: it?.precioRef && it.precioRef > 0 ? it.precioRef : 0,
    precioOrigen: 'plantilla',
    plantillaItemId: it?.id || null,
    fuente: it?.fuente || ''
  };
}

/** Las secciones de la plantilla como secciones vacias del armador, en su orden. */
export function seccionesParaArmar(plantilla) {
  return lista(plantilla?.secciones).map((s) => ({ nombre: s.nombre, plantillaClave: s.clave, items: [] }));
}

const CODIGO = /^[A-Za-z]{1,6}[ -]?\d{1,6}[A-Za-z]?$/;

/**
 * Los errores del formulario, uno por campo y dicho para el que lo llena. {} si esta bien.
 * Codigo: letras y numero (ej. JJ 1347). Proyecto y cliente: minimo 2 letras. Area: un numero mayor que cero (coma o punto).
 */
export function erroresDelFormulario({ codigo, proyecto, cliente, areaM2 } = {}) {
  const e = {};
  const c = String(codigo ?? '').trim();
  if (!c) e.codigo = 'Falta el codigo (ej. JJ 1347).';
  else if (!CODIGO.test(c)) e.codigo = 'El codigo va con letras y numero, por ejemplo JJ 1347.';
  if (String(proyecto ?? '').trim().length < 2) e.proyecto = 'Falta el nombre del proyecto (ej. CHOIBA).';
  if (String(cliente ?? '').trim().length < 2) e.cliente = 'Falta a nombre de quien va (ej. Maderinco S.A.S.).';
  const a = typeof areaM2 === 'number' ? areaM2 : Number(String(areaM2 ?? '').replace(/\./g, '').replace(',', '.'));
  if (!(a > 0)) e.area = 'Falta el area en m² (un numero, ej. 64,70).';
  return e;
}

/** Las palabras de la pantalla: en el demo de Maderinco se dice "item"; en los demas negocios, como hoy. */
export function palabras(esDemo) {
  return esDemo
    ? { plural: 'items', Plural: 'Items', mis: 'Mis items', tus: 'Tus items y precios', ninguno: 'Sin items todavia.' }
    : { plural: 'productos', Plural: 'Productos', mis: 'Mis productos', tus: 'Tus productos y precios', ninguno: 'Sin productos todavia.' };
}
